import { useQuery } from "@tanstack/react-query";
import i18next from "i18next";
import _ from "lodash";
import QueryString from "qs";
import { useMemo } from "react";

import useLabels from "./useLabels";
import { FACILITY_SORT, ORDER, FACILITY_SORT_BY } from "../../../config";
import { FacilityFilterConditionData } from "../../../models/FacilityFilterConditionData";
import { HealthIndexData } from "../../../models/HealthIndexData";
import DataSvc from "../../../services/dataSvc";
import LocalStorageSvc from "../../../services/localStorageSvc";

interface IFilterFormData {
  searchName: string;
  sort: typeof FACILITY_SORT;
  order: typeof ORDER;
  sortBy: typeof FACILITY_SORT_BY;
  numberPerPage: number;
  pageIndex: number;
}

const useLocations = (
  organizationId: number | undefined,
  searchQuery: QueryString.ParsedQs,
  filterFormData: IFilterFormData,
  conditionFormData: FacilityFilterConditionData
) => {
  const { data: labels, isLoading: labelsLoading } = useLabels({
    organization_id: organizationId
  });

  const queryParams = useMemo(() => {
    const label_id = searchQuery.label_id
      ? _.concat([], searchQuery.label_id as string).filter((id) =>
          labels?.some((label) => Number(label.id) === Number(id))
        )
      : null;
    return _.pickBy(
      {
        sort: filterFormData.sort,
        order: filterFormData.order,
        limit: filterFormData.numberPerPage,
        offset: (filterFormData.pageIndex - 1) * filterFormData.numberPerPage,
        organization_id: organizationId ? [organizationId] : organizationId,
        seek_total_count: true,
        label_id: label_id,
        category_id: searchQuery.category_id
          ? _.concat([], searchQuery.category_id)
          : null,
        municipality_id: searchQuery.municipality_id
          ? _.concat([], searchQuery.municipality_id)
          : null,
        query: filterFormData.searchName.trim(),
        score: getScoreArrayString(conditionFormData.healthIndex),
        ...getProfBoolQueryObject(conditionFormData),
        ...(searchQuery.has_lodging
          ? { has_lodging: searchQuery.has_lodging === "true" }
          : {})
      },
      (value: any) => {
        return !_.isUndefined(value) && !_.isNull(value);
      }
    );
  }, [conditionFormData, filterFormData, organizationId, searchQuery, labels]);

  const query = useQuery(
    ["locations", queryParams],
    () => {
      LocalStorageSvc.setItem("FacilityListPage_Config", {
        filterFormData,
        searchQuery
      });
      return DataSvc.getLocations(queryParams);
    },
    {
      enabled: !labelsLoading
    }
  );

  return query;
};

const getScoreArrayString = (healthIndexData: HealthIndexData[]) => {
  if (!healthIndexData || healthIndexData.length === 0) {
    return [];
  }
  const scoreArrayString: string[] = [];
  const h = (key: string) =>
    i18next.t(`facilityListPage.modalFilterCondition.healthIndexLabels.${key}`);
  healthIndexData.forEach((element) => {
    if (element.checked) {
      switch (element.name) {
        case h("A"):
          scoreArrayString.push("A1");
          break;
        case h("B"):
          scoreArrayString.push("B1");
          break;
        case h("C"):
          scoreArrayString.push("C1");
          scoreArrayString.push("C2");
          break;
        case h("D"):
          scoreArrayString.push("D1");
          break;
      }
    }
  });
  return scoreArrayString;
};

const getProfBoolQueryObject = (
  conditionData: FacilityFilterConditionData
): Record<string, boolean> => {
  const obj: Record<string, boolean> = {};
  conditionData.profBooleans.forEach(({ key, shouldInclude }) => {
    if (_.isBoolean(shouldInclude)) {
      obj[key] = shouldInclude;
    }
  });
  return obj;
};

export default useLocations;
